// AstriaVietnamWizard.tsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronLeft, ChevronRight, Check } from "lucide-react";
import {
  SHELL_COLORS_ASTRIA_VIETNAM,
  ASTRIA_VIETNAM_HEADING_FONT,
} from "./AstriaVietnamShell";

// ─── Astria Vietnam — guided question-flow primitives ─────────────────────
// Step-by-step wizard: one question per screen, animated progress bar,
// Back/Next controls. Dropdown, Chips, Date Fields (day/month/year + Chi).
// Structurally cloned from AstriaIndiaV2Wizard.tsx, re-themed to Vietnam's
// soft pink/rose palette (SHELL_COLORS_ASTRIA_VIETNAM has no separate
// "gold" accent like India — `primary` is used as the secondary gradient
// stop everywhere India used `gold`).

interface WizardProgressProps {
  step: number; // 1-indexed
  total: number;
  accent?: string;
  stepLabel?: (step: number, total: number) => string;
}

export const WizardProgress: React.FC<WizardProgressProps> = ({
  step,
  total,
  accent = SHELL_COLORS_ASTRIA_VIETNAM.accent,
  stepLabel = (s, t) => `Step ${s} of ${t}`,
}) => {
  const pct = Math.round((step / total) * 100);
  return (
    <div style={{ marginBottom: 28 }}>
      <div
        className="flex items-center justify-between"
        style={{
          marginBottom: 8,
          fontSize: 12,
          color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
        }}
      >
        <span>{stepLabel(step, total)}</span>
        <span>{pct}%</span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: SHELL_COLORS_ASTRIA_VIETNAM.section,
          overflow: "hidden",
        }}
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{
            height: "100%",
            borderRadius: 999,
            background: `linear-gradient(90deg, ${accent}, ${SHELL_COLORS_ASTRIA_VIETNAM.primary})`,
          }}
        />
      </div>
    </div>
  );
};

interface WizardStepProps {
  stepKey: string; // used as the animation key so steps swap with a slide/fade
  question: string;
  helper?: string;
  children: React.ReactNode;
}

export const WizardStep: React.FC<WizardStepProps> = ({
  stepKey,
  question,
  helper,
  children,
}) => (
  <AnimatePresence mode="wait">
    <motion.div
      key={stepKey}
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -16 }}
      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
    >
      <h2
        style={{
          fontSize: 21,
          fontWeight: 700,
          color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
          fontFamily: ASTRIA_VIETNAM_HEADING_FONT,
          marginBottom: helper ? 6 : 20,
          lineHeight: 1.35,
        }}
      >
        {question}
      </h2>
      {helper && (
        <p
          style={{
            fontSize: 13.5,
            color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
            marginBottom: 20,
            lineHeight: 1.6,
          }}
        >
          {helper}
        </p>
      )}
      {children}
    </motion.div>
  </AnimatePresence>
);

interface WizardNavProps {
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
  backLabel?: string;
  nextDisabled?: boolean;
  showBack?: boolean;
}

export const WizardNav: React.FC<WizardNavProps> = ({
  onBack,
  onNext,
  nextLabel = "Continue",
  backLabel = "Back",
  nextDisabled = false,
  showBack = true,
}) => (
  <div className="flex items-center gap-3" style={{ marginTop: 28 }}>
    {showBack && onBack && (
      <button
        onClick={onBack}
        className="flex items-center gap-1 rounded-full text-sm font-semibold transition-all hover:bg-black/[0.03]"
        style={{
          paddingInline: 18,
          paddingBlock: 12,
          color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
          border: `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
        }}
      >
        <ChevronLeft className="w-4 h-4" />
        {backLabel}
      </button>
    )}
    <button
      onClick={onNext}
      disabled={nextDisabled}
      className="flex items-center justify-center gap-1.5 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
      style={{
        flex: 1,
        paddingBlock: 13,
        background: `linear-gradient(135deg, ${SHELL_COLORS_ASTRIA_VIETNAM.accent} 0%, ${SHELL_COLORS_ASTRIA_VIETNAM.primary} 100%)`,
        boxShadow: `0 6px 18px ${SHELL_COLORS_ASTRIA_VIETNAM.accentSoft}`,
      }}
    >
      {nextLabel}
      <ChevronRight className="w-4 h-4" />
    </button>
  </div>
);

// ─── Dropdown ───────────────────────────────────────────────────────────
interface DropdownOption {
  value: string;
  label: string;
}

export const WizardDropdown: React.FC<{
  options: DropdownOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ options, value, onChange, placeholder = "Select an option" }) => {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between rounded-2xl text-left transition-all"
        style={{
          padding: "16px 18px",
          background: SHELL_COLORS_ASTRIA_VIETNAM.white,
          border: `1.5px solid ${
            open
              ? SHELL_COLORS_ASTRIA_VIETNAM.accent
              : SHELL_COLORS_ASTRIA_VIETNAM.cardBorder
          }`,
          boxShadow: open ? SHELL_COLORS_ASTRIA_VIETNAM.accentGlow : "none",
        }}
      >
        <span
          style={{
            fontSize: 15,
            color: selected
              ? SHELL_COLORS_ASTRIA_VIETNAM.titleColor
              : SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className="w-4 h-4"
          style={{
            color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
          }}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              right: 0,
              zIndex: 10,
              background: SHELL_COLORS_ASTRIA_VIETNAM.white,
              border: `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
              borderRadius: 16,
              boxShadow: "0 12px 32px rgba(58,47,51,0.14)",
              overflow: "hidden",
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between text-left transition-colors hover:bg-black/[0.02]"
                style={{
                  padding: "13px 18px",
                  fontSize: 14.5,
                  color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
                  background:
                    opt.value === value
                      ? SHELL_COLORS_ASTRIA_VIETNAM.section
                      : "transparent",
                }}
              >
                {opt.label}
                {opt.value === value && (
                  <Check
                    className="w-4 h-4"
                    style={{ color: SHELL_COLORS_ASTRIA_VIETNAM.accent }}
                  />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Choice chips (single-select) ────────────────────────────────────────
export const WizardChips: React.FC<{
  options: DropdownOption[];
  value: string | null;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className="rounded-full text-sm font-medium transition-all"
          style={{
            paddingInline: 18,
            paddingBlock: 11,
            color: active ? "#fff" : SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
            background: active
              ? `linear-gradient(135deg, ${SHELL_COLORS_ASTRIA_VIETNAM.accent} 0%, ${SHELL_COLORS_ASTRIA_VIETNAM.primary} 100%)`
              : SHELL_COLORS_ASTRIA_VIETNAM.section,
            border: active
              ? "none"
              : `1px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
            boxShadow: active
              ? `0 4px 14px ${SHELL_COLORS_ASTRIA_VIETNAM.accentSoft}`
              : "none",
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ─── Plain text input ─────────────────────────────────────────────────────
export const WizardTextInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{
      width: "100%",
      padding: "16px 18px",
      fontSize: 15,
      borderRadius: 16,
      border: `1.5px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
      background: SHELL_COLORS_ASTRIA_VIETNAM.white,
      color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
      outline: "none",
      fontFamily: "inherit",
    }}
  />
);

// ─── Numeric field (day/month/year building block) ────────────────────────
const WizardNumberInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  max: number;
}> = ({ value, onChange, placeholder, max }) => (
  <input
    type="number"
    inputMode="numeric"
    min={1}
    max={max}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{
      width: "100%",
      padding: "16px 12px",
      fontSize: 15,
      textAlign: "center",
      borderRadius: 16,
      border: `1.5px solid ${SHELL_COLORS_ASTRIA_VIETNAM.cardBorder}`,
      background: SHELL_COLORS_ASTRIA_VIETNAM.white,
      color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
      outline: "none",
      fontFamily: "inherit",
    }}
  />
);

// ─── Date fields — day/month/year number inputs + a Chi (birth hour)
// dropdown, per Vi.txt's own UI spec (separate numeric fields, not a
// single free-text date string like India's WizardTextInput DOB pattern).
// Emits a normalized "DD/MM/YYYY" string via `onDateChange`, matching the
// wire format backend/helper/vietnam/vietnamTuViChart.js expects.
export interface WizardDateFieldsValue {
  day: string;
  month: string;
  year: string;
  hourChi: string | null;
}

export const WizardDateFields: React.FC<{
  value: WizardDateFieldsValue;
  onChange: (value: WizardDateFieldsValue) => void;
  hourOptions: DropdownOption[];
  hourLabel: string;
  hourPlaceholder: string;
  dayPlaceholder: string;
  monthPlaceholder: string;
  yearPlaceholder: string;
  showHour?: boolean;
}> = ({
  value,
  onChange,
  hourOptions,
  hourLabel,
  hourPlaceholder,
  dayPlaceholder,
  monthPlaceholder,
  yearPlaceholder,
  showHour = true,
}) => (
  <div>
    <div className="flex gap-3" style={{ marginBottom: showHour ? 18 : 0 }}>
      <WizardNumberInput
        value={value.day}
        onChange={(day) => onChange({ ...value, day })}
        placeholder={dayPlaceholder}
        max={31}
      />
      <WizardNumberInput
        value={value.month}
        onChange={(month) => onChange({ ...value, month })}
        placeholder={monthPlaceholder}
        max={12}
      />
      <WizardNumberInput
        value={value.year}
        onChange={(year) => onChange({ ...value, year })}
        placeholder={yearPlaceholder}
        max={9999}
      />
    </div>
    {showHour && (
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
            marginBottom: 8,
          }}
        >
          {hourLabel}
        </div>
        <WizardDropdown
          options={hourOptions}
          value={value.hourChi}
          onChange={(hourChi) => onChange({ ...value, hourChi })}
          placeholder={hourPlaceholder}
        />
      </div>
    )}
  </div>
);

/**
 * Normalizes a WizardDateFieldsValue into the "DD/MM/YYYY" wire format the
 * backend parses, or null if day/month/year aren't all present/valid.
 */
export function formatDateFieldsValue(
  value: WizardDateFieldsValue,
): string | null {
  const day = parseInt(value.day, 10);
  const month = parseInt(value.month, 10);
  const year = parseInt(value.year, 10);
  if (
    !Number.isFinite(day) ||
    !Number.isFinite(month) ||
    !Number.isFinite(year) ||
    day < 1 ||
    day > 31 ||
    month < 1 ||
    month > 12 ||
    year < 1
  ) {
    return null;
  }
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
}
